import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import jobService from '@/services/jobService';

const initialState = {
  jobs: [],
  featuredJobs: [],
  trendingJobs: [],
  currentJob: null,
  savedJobs: [],
  pagination: null,
  filters: {
    q: '',
    location: '',
    jobType: [],
    workMode: [],
    experience: '',
    salary: '',
    industry: '',
    category: '',
    postedWithin: '',
  },
  isLoading: false,
  error: null,
};

export const fetchJobs = createAsyncThunk(
  'jobs/fetchJobs',
  async (params, { rejectWithValue }) => {
    try {
      const response = await jobService.getJobs(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch jobs');
    }
  }
);

export const fetchJobById = createAsyncThunk(
  'jobs/fetchJobById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await jobService.getJobById(id);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch job');
    }
  }
);

export const searchJobs = createAsyncThunk(
  'jobs/searchJobs',
  async (params, { rejectWithValue }) => {
    try {
      const response = await jobService.searchJobs(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Search failed');
    }
  }
);

export const fetchFeaturedJobs = createAsyncThunk(
  'jobs/fetchFeaturedJobs',
  async (limit = 10, { rejectWithValue }) => {
    try {
      const response = await jobService.getFeaturedJobs(limit);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch featured jobs');
    }
  }
);

export const fetchTrendingJobs = createAsyncThunk(
  'jobs/fetchTrendingJobs',
  async (limit = 10, { rejectWithValue }) => {
    try {
      const response = await jobService.getTrendingJobs(limit);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch trending jobs');
    }
  }
);

export const saveJob = createAsyncThunk(
  'jobs/saveJob',
  async (jobId, { rejectWithValue }) => {
    try {
      await jobService.saveJob(jobId);
      return jobId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to save job');
    }
  }
);

export const unsaveJob = createAsyncThunk(
  'jobs/unsaveJob',
  async (jobId, { rejectWithValue }) => {
    try {
      await jobService.unsaveJob(jobId);
      return jobId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to unsave job');
    }
  }
);

export const fetchSavedJobs = createAsyncThunk(
  'jobs/fetchSavedJobs',
  async (params, { rejectWithValue }) => {
    try {
      const response = await jobService.getSavedJobs(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch saved jobs');
    }
  }
);

const jobSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    clearCurrentJob: (state) => {
      state.currentJob = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Jobs
      .addCase(fetchJobs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.jobs = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Fetch Job By ID
      .addCase(fetchJobById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchJobById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentJob = action.payload;
      })
      .addCase(fetchJobById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Search Jobs
      .addCase(searchJobs.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(searchJobs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.jobs = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(searchJobs.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Featured Jobs
      .addCase(fetchFeaturedJobs.fulfilled, (state, action) => {
        state.featuredJobs = action.payload;
      })
      // Trending Jobs
      .addCase(fetchTrendingJobs.fulfilled, (state, action) => {
        state.trendingJobs = action.payload;
      })
      // Save Job
      .addCase(saveJob.fulfilled, (state, action) => {
        if (state.currentJob && state.currentJob._id === action.payload) {
          state.currentJob.hasSaved = true;
        }
      })
      // Unsave Job
      .addCase(unsaveJob.fulfilled, (state, action) => {
        if (state.currentJob && state.currentJob._id === action.payload) {
          state.currentJob.hasSaved = false;
        }
        state.savedJobs = state.savedJobs.filter((j) => j.job._id !== action.payload);
      })
      // Saved Jobs
      .addCase(fetchSavedJobs.fulfilled, (state, action) => {
        state.savedJobs = action.payload.data;
      });
  },
});

export const { setFilters, clearFilters, clearCurrentJob, clearError } = jobSlice.actions;
export default jobSlice.reducer;
